import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { SessionResult } from '@/constants'
import { useSessionStore } from './useSessionStore'

type LifelineCallback = (result: SessionResult, message: string) => void

// Auto-dismiss timers keyed by toast id. Pauses when the tab is hidden so
// a toast shown right before the user switches tabs is still visible when they come back.
const dismissCleanup = new Map<string, () => void>()

function scheduleDismiss(id: string, delay: number, onExpire: () => void) {
  if (typeof document === 'undefined') {
    setTimeout(onExpire, delay)
    return
  }
  let remaining = delay
  let startedAt = Date.now()
  let timer: ReturnType<typeof setTimeout> | null = null

  const cleanup = () => {
    if (timer) {
      clearTimeout(timer)
    }
    document.removeEventListener('visibilitychange', onVisibility)
    dismissCleanup.delete(id)
  }

  const fire = () => {
    cleanup()
    onExpire()
  }

  const pause = () => {
    if (!timer) {
      return
    }
    clearTimeout(timer)
    timer = null
    remaining = Math.max(0, remaining - (Date.now() - startedAt))
  }

  const resume = () => {
    if (timer) {
      return
    }
    startedAt = Date.now()
    timer = setTimeout(fire, remaining)
  }

  const onVisibility = () => (document.hidden ? pause() : resume())

  document.addEventListener('visibilitychange', onVisibility)
  dismissCleanup.set(id, cleanup)
  if (!document.hidden) {
    resume()
  }
}

function cancelDismiss(id: string) {
  dismissCleanup.get(id)?.()
}

// UI-related types
export interface StyleVariables {
  [key: string]: string
}

export interface StyleLabels {
  [key: string]: { [subKey: string]: string }
}

export interface UIMessage {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title?: string
  message: string
  timestamp: number
  persistent?: boolean
  /** Custom auto-dismiss duration in ms. Falls back to 7000 for errors, 5000 otherwise. */
  duration?: number
}

export interface PopupDisplayState {
  isVisible: boolean
  title?: string
  content?: string
  type?: 'info' | 'warning' | 'error' | 'confirm'
}

export interface UIStore {
  // State
  isLoading: boolean
  isCPLoading: boolean
  initialLoading: boolean
  isPopupLoading: boolean
  forceDisplayLoader: boolean
  error: any | null
  criticalError: any | null
  lifelineError: any | null
  messages: string[]
  warnings: string[]
  warning: string | null
  uiMessages: UIMessage[]
  popupDisplayState: PopupDisplayState | null

  // Style and branding
  styleVariables: StyleVariables
  styleLabels: StyleLabels
  style: string
  styleUrl: string | null
  faviconUrl: string | null

  // Feature flags and settings
  profilerEnabled: boolean
  cprofilerEnabled: boolean
  debug: boolean
  isIE: boolean
  captchaLoaded: boolean
  refreshSessionOnUndo: boolean
  subscriptionOnly: boolean
  isLanguageReady: boolean

  // Display states
  showChatbotHeader: boolean
  isBannerHidden: boolean
  isLabelHidden: boolean
  isContentHidden: boolean
  isMediaHidden: boolean

  // Timer management
  idleTimer: number | null
  lifelineCallback: LifelineCallback | null

  // Computed selectors
  isError: () => boolean
  hasMessages: () => boolean
  hasWarnings: () => boolean
  isAnyLoading: () => boolean
  getMessagesByType: (type: 'info' | 'success' | 'warning' | 'error') => UIMessage[]
  getLatestMessage: () => UIMessage | null

  // Actions for loading states
  setLoading: (loading: boolean) => void
  setCPLoading: (loading: boolean) => void
  setInitialLoading: (loading: boolean) => void
  setPopupLoading: (loading: boolean) => void
  setForceDisplayLoader: (force: boolean) => void

  // Actions for errors and messages
  setError: (error: any) => void
  setCriticalError: (error: any) => void
  setLifelineError: (error: any) => void
  clearError: () => void
  setMessages: (messages: string[]) => void
  setWarnings: (warnings: string[]) => void
  setWarning: (warning: string | null) => void
  clearWarning: () => void
  clearMessages: () => void
  addUIMessage: (message: Omit<UIMessage, 'id' | 'timestamp'>) => string | undefined
  removeUIMessage: (id: string) => void
  clearUIMessages: () => void

  // Toast notification actions
  showSuccessToast: (message: string) => void
  showErrorToast: (error: any) => void
  showWarningToast: (message: string) => void
  showInfoToast: (message: string) => void

  // Actions for popup state
  setPopupDisplayState: (state: PopupDisplayState | null) => void
  showPopup: (title: string, content: string, type?: 'info' | 'warning' | 'error' | 'confirm') => void
  hidePopup: () => void

  // Actions for style and branding
  setStyleVariables: (variables: StyleVariables) => void
  setStyleLabels: (labels: StyleLabels) => void
  setStyle: (style: string) => void
  setStyleUrl: (url: string | null) => void
  setFaviconUrl: (url: string | null) => void

  // Actions for feature flags
  setProfilerEnabled: (enabled: boolean) => void
  setCProfilerEnabled: (enabled: boolean) => void
  setDebug: (debug: boolean) => void
  setIE: (isIE: boolean) => void
  setCaptchaLoaded: (loaded: boolean) => void
  setLanguageReady: (ready: boolean) => void
  setRefreshSessionOnUndo: (refresh: boolean) => void
  setSubscriptionOnly: (subscriptionOnly: boolean) => void

  // Actions for display states
  setShowChatbotHeader: (show: boolean) => void
  setBannerHidden: (hidden: boolean) => void
  setLabelHidden: (hidden: boolean) => void
  setContentHidden: (hidden: boolean) => void
  setMediaHidden: (hidden: boolean) => void

  // Timer actions
  setIdleTimer: (timer: number | null) => void
  resetIdleTimer: () => void
  setLifelineCallback: (callback: LifelineCallback | null) => void

  // Utility actions
  ready: () => void
  success: () => void
}

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        isLoading: false,
        isCPLoading: false,
        initialLoading: import.meta.env.DEV ? false : true,
        isPopupLoading: false,
        forceDisplayLoader: false,
        error: null,
        criticalError: null,
        lifelineError: null,
        messages: [],
        warnings: [],
        warning: null,
        uiMessages: [],
        popupDisplayState: null,

        // Style and branding
        styleVariables: {},
        styleLabels: {},
        style: '',
        styleUrl: null,
        faviconUrl: null,

        // Feature flags and settings
        profilerEnabled: false,
        cprofilerEnabled: false,
        debug: false,
        isIE: false,
        captchaLoaded: false,
        refreshSessionOnUndo: false,
        subscriptionOnly: false,
        isLanguageReady: false,

        // Display states
        showChatbotHeader: false,
        isBannerHidden: false,
        isLabelHidden: false,
        isContentHidden: false,
        isMediaHidden: false,
        // Timer management
        idleTimer: null,
        lifelineCallback: null,

        // Computed selectors
        isError: () => {
          const { error, criticalError } = get()
          return !!(error || criticalError)
        },

        hasMessages: () => {
          const { messages, uiMessages } = get()
          return messages.length > 0 || uiMessages.length > 0
        },

        hasWarnings: () => {
          const { warnings } = get()
          return warnings.length > 0
        },

        isAnyLoading: () => {
          const { isLoading, isCPLoading, initialLoading, isPopupLoading, forceDisplayLoader } = get()
          return isLoading || isCPLoading || initialLoading || isPopupLoading || forceDisplayLoader
        },

        getMessagesByType: (type: 'info' | 'success' | 'warning' | 'error') => {
          const { uiMessages } = get()
          return uiMessages.filter(msg => msg.type === type)
        },

        getLatestMessage: () => {
          const { uiMessages } = get()
          if (uiMessages.length === 0) {
            return null
          }
          return uiMessages.reduce((latest, msg) => (msg.timestamp > latest.timestamp ? msg : latest))
        },

        // Actions for loading states
        setLoading: (loading: boolean) =>
          set(state => {
            if (!loading && state.forceDisplayLoader) {
              return
            }
            state.isLoading = loading
          }),

        setCPLoading: (loading: boolean) =>
          set(state => {
            if (!loading && state.forceDisplayLoader) {
              return
            }
            state.isCPLoading = loading
          }),

        setInitialLoading: (loading: boolean) =>
          set(state => {
            state.initialLoading = loading
          }),

        setPopupLoading: (loading: boolean) =>
          set(state => {
            state.isPopupLoading = loading
          }),

        setForceDisplayLoader: (force: boolean) =>
          set(state => {
            state.forceDisplayLoader = force
          }),

        // Actions for errors and messages
        setError: (error: any) =>
          set(state => {
            state.error = error
            state.criticalError = null
            console.error('UI Error set:', error)
          }),

        setCriticalError: (error: any) => {
          set(state => {
            state.criticalError = error
            state.error = null
            state.isLoading = false
          })
          // Fire lifeline only for a real error the campaign opts into. Clearing the
          // error (setCriticalError(null)) must not report a LifelineError.
          const session = useSessionStore.getState().session as any
          if (error && session?.can_enter_error_lifeline) {
            const cb = get().lifelineCallback
            if (typeof cb === 'function') {
              cb(SessionResult.LifelineError, error?.message || error?.toString?.() || 'Critical error')
            }
          }
        },

        setLifelineError: (error: any) =>
          set(state => {
            state.lifelineError = error
          }),

        clearError: () =>
          set(state => {
            state.error = null
            state.criticalError = null
          }),

        // Producer entry points — write directly into uiMessages so there's a
        // single store slice for any toast/banner consumer to read.
        setMessages: (messages: string[]) => {
          messages.forEach(m => get().addUIMessage({ type: 'success', message: m }))
        },

        setWarnings: (warnings: string[]) => {
          warnings.forEach(m => get().addUIMessage({ type: 'warning', message: m }))
        },

        setWarning: (warning: string | null) =>
          set(state => {
            state.warning = warning
          }),

        clearWarning: () =>
          set(state => {
            state.warning = null
          }),

        clearMessages: () =>
          set(state => {
            state.messages = []
            state.warnings = []
            state.error = null
            state.criticalError = null
          }),

        addUIMessage: (message: Omit<UIMessage, 'id' | 'timestamp'>) => {
          const now = Date.now()
          const existing = get().uiMessages
          const duplicate = existing.find(
            m => m.type === message.type && m.message === message.message && now - m.timestamp < 1500
          )
          if (duplicate) {
            return duplicate.id
          }

          const newMessage: UIMessage = {
            ...message,
            id: `msg_${now}_${Math.random().toString(36).slice(2, 11)}`,
            timestamp: now,
          }

          set(state => {
            state.uiMessages.push(newMessage)
          })

          if (!message.persistent) {
            const delay = message.duration ?? (message.type === 'error' ? 7000 : 5000)
            scheduleDismiss(newMessage.id, delay, () => get().removeUIMessage(newMessage.id))
          }

          return newMessage.id
        },

        removeUIMessage: (id: string) => {
          cancelDismiss(id)
          set(state => {
            const index = state.uiMessages.findIndex(msg => msg.id === id)
            if (index >= 0) {
              state.uiMessages.splice(index, 1)
            }
          })
        },

        clearUIMessages: () => {
          get().uiMessages.forEach(m => cancelDismiss(m.id))
          set(state => {
            state.uiMessages = []
          })
        },

        showSuccessToast: (message: string) => {
          get().addUIMessage({ type: 'success', message })
        },

        showErrorToast: (error: any) => {
          const message = typeof error === 'string' ? error : error?.message || 'Something went wrong.'
          get().addUIMessage({ type: 'error', message })
        },

        showWarningToast: (message: string) => {
          get().addUIMessage({ type: 'warning', message })
        },

        showInfoToast: (message: string) => {
          get().addUIMessage({ type: 'info', message })
        },

        // Actions for popup state
        setPopupDisplayState: (popupState: PopupDisplayState | null) =>
          set(state => {
            state.popupDisplayState = popupState
          }),

        showPopup: (title: string, content: string, type: 'info' | 'warning' | 'error' | 'confirm' = 'info') =>
          set(state => {
            state.popupDisplayState = {
              isVisible: true,
              title,
              content,
              type,
            }
          }),

        hidePopup: () =>
          set(state => {
            state.popupDisplayState = null
          }),

        // Actions for style and branding
        setStyleVariables: (variables: StyleVariables) =>
          set(state => {
            state.styleVariables = variables
          }),

        setStyleLabels: (labels: StyleLabels) =>
          set(state => {
            state.styleLabels = labels
          }),

        setStyle: (style: string) =>
          set(state => {
            state.style = style
          }),

        setStyleUrl: (url: string | null) =>
          set(state => {
            state.styleUrl = url
          }),

        setFaviconUrl: (url: string | null) =>
          set(state => {
            state.faviconUrl = url
          }),

        // Actions for feature flags
        setProfilerEnabled: (enabled: boolean) =>
          set(state => {
            state.profilerEnabled = enabled
          }),

        setCProfilerEnabled: (enabled: boolean) =>
          set(state => {
            state.cprofilerEnabled = enabled
          }),

        setDebug: (debug: boolean) =>
          set(state => {
            state.debug = debug
          }),

        setIE: (isIE: boolean) =>
          set(state => {
            state.isIE = isIE
          }),

        setCaptchaLoaded: (loaded: boolean) =>
          set(state => {
            state.captchaLoaded = loaded
          }),

        setLanguageReady: (ready: boolean) =>
          set(state => {
            state.isLanguageReady = ready
          }),

        setRefreshSessionOnUndo: (refresh: boolean) =>
          set(state => {
            state.refreshSessionOnUndo = refresh
          }),

        setSubscriptionOnly: (subscriptionOnly: boolean) =>
          set(state => {
            state.subscriptionOnly = subscriptionOnly
          }),

        // Actions for display states
        setShowChatbotHeader: (show: boolean) =>
          set(state => {
            state.showChatbotHeader = show
          }),

        setBannerHidden: (hidden: boolean) =>
          set(state => {
            state.isBannerHidden = hidden
          }),

        setLabelHidden: (hidden: boolean) =>
          set(state => {
            state.isLabelHidden = hidden
          }),

        setContentHidden: (hidden: boolean) =>
          set(state => {
            state.isContentHidden = hidden
          }),

        setMediaHidden: (hidden: boolean) =>
          set(state => {
            state.isMediaHidden = hidden
          }),

        // Timer actions
        setIdleTimer: (timer: number | null) =>
          set(state => {
            if (state.idleTimer) {
              clearTimeout(state.idleTimer)
            }
            state.idleTimer = timer
          }),

        resetIdleTimer: () =>
          set(state => {
            if (state.idleTimer) {
              clearTimeout(state.idleTimer)
              state.idleTimer = null
            }
            // Could implement idle timer reset logic here
          }),

        setLifelineCallback: (callback: LifelineCallback | null) =>
          set(state => {
            state.lifelineCallback = callback
          }),

        // Utility actions
        ready: () =>
          set(state => {
            if (state.forceDisplayLoader) {
              return
            }
            state.isLoading = false
            state.initialLoading = false
            state.isPopupLoading = false
            state.isCPLoading = false
          }),

        success: () =>
          set(state => {
            state.isLoading = false
            state.error = null
            state.criticalError = null
            state.messages = []
            state.warnings = []
          }),
      })),
      {
        name: 'ivrpath-ui',
        storage: createJSONStorage(() => localStorage),
        // Persist UI preferences and settings
        partialize: state => ({
          styleVariables: state.styleVariables,
          styleLabels: state.styleLabels,
          style: state.style,
          styleUrl: state.styleUrl,
          faviconUrl: state.faviconUrl,
          profilerEnabled: state.profilerEnabled,
          cprofilerEnabled: state.cprofilerEnabled,
          debug: state.debug,
          captchaLoaded: state.captchaLoaded,
          subscriptionOnly: state.subscriptionOnly,
          showChatbotHeader: state.showChatbotHeader,
          isBannerHidden: state.isBannerHidden,
          isLabelHidden: state.isLabelHidden,
          isContentHidden: state.isContentHidden,
          isMediaHidden: state.isMediaHidden,
          // Don't persist loading states, errors, or timers
        }),
      }
    ),
    { name: 'UIStore', enabled: import.meta.env.DEV }
  )
)

// Selector hooks for common use cases
export const useIsLoading = () => useUIStore(state => state.isAnyLoading())
export const useError = () => useUIStore(state => state.error)
export const useCriticalError = () => useUIStore(state => state.criticalError)
export const useMessages = () => useUIStore(state => state.messages)
export const useWarnings = () => useUIStore(state => state.warnings)
export const useUIMessages = () => useUIStore(state => state.uiMessages)
export const usePopupDisplayState = () => useUIStore(state => state.popupDisplayState)
export const useStyleVariables = () => useUIStore(state => state.styleVariables)
export const useStyleLabels = () => useUIStore(state => state.styleLabels)
export const useDebug = () => useUIStore(state => state.debug)
export const useIsIE = () => useUIStore(state => state.isIE)
